export interface ITxnCollection {
  edges: ITxnEdge[];
}

export interface ITxnEdge {
  node: ITxn;
}

export interface ITxn {
  id: number;
  memberID: number;
  referenceNumber: string;
  type: string;
  itemCount: number;
  externalTxnID?: string;
  cents: number;
  points: number;
  createdAt: Date;
  updatedAt: Date;
  txnDelivery?: ITxnDeliveryEdge;
  txnItems?: ITxnItemCollection;
}

export interface ITxnItemCollection {
  edges: ITxnItemEdge[];
}

export interface ITxnItemEdge {
  node: ITxnItem;
}

export interface ITxnItem {
  id: number;
  cents: number;
  points: number;
  item: IItemEdge;
  createdAt: Date;
  updatedAt: Date;
}

export interface ITxnDeliveryEdge {
  node: ITxnDelivery;
}

export interface ITxnDelivery {
  status: string;
  type: string;
}

export interface ISwapStats {
  inItemCount: number;
  outItemCount: number;
  pointsEarned: number;
  pointsUsed: number;
}

export interface IItemCollection {
  edges: IItemEdge[];
}

export interface IItemEdge {
  node: IItem;
}

export interface IItem {
  id: number;
  sku: string;
  name?: string;
  description?: string;
  itemCategoryID: number;
  itemSubCategoryID: number;
  itemQualityID: number;
  itemStyleID: number;
  itemBrandCategoryID: number;
  itemSizeID: number;
  itemMaterialID?: number;
  brand?: string;
  points: number;
  cents: number;
  metadata?: any;
}

export interface ICartItemCollection {
  edges: ICartItemEdge[];
}

export interface ICartItemEdge {
  node: ICartItem;
}

export interface ICartItem {
  id: number;
  item: IItemEdge;
  isAvailable: boolean;
}

export interface ISearchItemsInput {
  itemCategoryID?: number;
  itemSubCategoryID?: number;
  itemQualityID_in?: number[];
  itemStyleID?: number;
  itemBrandCategoryID?: number;
  itemSizeID?: number;
  itemMaterialID?: number;
  brand_contains?: string;
  points_lte?: number;
  points_gte?: number;
  cents_lte?: number;
  cents_gte?: number;
}

export interface IDeliveryDetailsInput {
  type: string;
}

export const publicSwapGQL: Record<string, string> = {
  searchItems: `query SearchItems($input: SearchItemsInput, $pageNumber: Int, $pageSize: Int) {
    countItems: countItems(input: $input)
    searchItems: items(input: $input, pageNumber: $pageNumber, pageSize: $pageSize) {
      edges {
        node {
          id
          sku
          name
          description
          itemCategoryID
          itemSubCategoryID
          itemQualityID
          itemStyleID
          itemBrandCategoryID
          itemSizeID
          itemMaterialID
          brand
          points
          cents
          metadata
        }
      }
    }
  }`,
  retrieveItemBySKU: `query RetrieveItemBySKU($sku: String!) {
    retrieveItemBySKU: itemBySKU(sku: $sku) {
      node {
        id
        sku
        name
        description
        itemCategoryID
        itemSubCategoryID
        itemQualityID
        itemStyleID
        itemBrandCategoryID
        itemSizeID
        itemMaterialID
        brand
        points
        cents
        metadata
      }
    }
  }`,
};

export const authenticatedSwapGQL: Record<string, string> = {
  retrieveAllTxns: `query RetrieveAllTxns() {
    retrieveAllTxns: txns {
      edges {
        node {
          id
          referenceNumber
          type
          itemCount
          externalTxnID
          cents
          points
          createdAt
        }
      }
    }
  }`,
  retrieveTxn: `query RetrieveTxn($id: Int!) {
    retrieveTxn: txn(id: $id) {
      node {
        id
        referenceNumber
        type
        itemCount
        externalTxnID
        cents
        points
        createdAt
        txnDelivery {
          node {
            type
            status
          }
        }
        txnItems {
          edges {
            node {
              id
              cents
              points
              createdAt
              updatedAt
              item {
                node {
                  id
                  sku
                  name
                  description
                  itemCategoryID
                  itemSubCategoryID
                  itemQualityID
                  itemStyleID
                  itemBrandCategoryID
                  itemSizeID
                  itemMaterialID
                  brand
                  points
                  cents
                  metadata
                }
              }
            }
          }
        }
      }
    }
  }`,
  retrieveSwapStats: `query RetrieveSwapStats() {
    retrieveSwapStats: swapStats {
      inItemCount
      outItemCount
      pointsEarned
      pointsUsed
    }
  }`,
  retrieveCart: `query RetrieveCart {
    retrieveCart: cart {
      edges {
        node {
          id
          item {
            node {
              id
              sku
              name
              description
              itemCategoryID
              itemSubCategoryID
              itemQualityID
              itemStyleID
              itemBrandCategoryID
              itemSizeID
              itemMaterialID
              brand
              points
              cents
              metadata
            }
          }
          isAvailable
        }
      }
    }
  }`,
  addToCart: `mutation AddToCart($itemID: Int!) {
    addToCart: addToCart(itemID: $itemID) 
  }`,
  removeFromCart: `mutation RemoveFromCart($itemID: Int!) {
    removeFromCart: removeFromCart(itemID: $itemID) {
      edges {
        node {
          id
          item {
            node {
              id
              sku
              name
              description
              itemCategoryID
              itemSubCategoryID
              itemQualityID
              itemStyleID
              itemBrandCategoryID
              itemSizeID
              itemMaterialID
              brand
              points
              cents
              metadata
            }
          }
          isAvailable
        }
      }
    }
  }`,
  verifyCart: `query VerifyCart() {
    verifyCart: verifyCart() 
  }`,
  checkout: `mutation Checkout($expectedTotalPoints: Int!, $expectedTotalCents: Int!, $externalTxnID: String, $delivery: DeliveryDetailsInput!) {
    checkout: checkout(expectedTotalPoints: $expectedTotalPoints, expectedTotalCents: $expectedTotalCents, externalTxnID: $externalTxnID, delivery: $delivery) {
      node {
        id
        referenceNumber
        type
        itemCount
        externalTxnID
        cents
        points
        createdAt
      }
    }
  }`,
};
